import React, { useState } from "react";
import Modal from "ui/Modal";
import { Drawer, DrawerContent } from "ui/Drawer";
import api from "config/axiosConfig";
import { useUser } from "util/UserContext";
import TextEditor from "components/TextEditor";
import { Info, CircleCheck, CircleX, Loader2 } from "lucide-react";
import { Impact } from "util/constants";
//import CoauthorSelect, { CoauthorSuggestion } from "components/Coauthors/CoauthorSelect";
//import EntityAvatar from "components/Entities/EntityAvatar";
import EntityPrivacy from "components/Entities/EntityPrivacy";
import AiArgumentEvaluationPanel from "components/AI/AiArgumentEvaluationPanel";
import { PrivacyLevel } from "util/userUtils";
import { cn } from "util/cn";
import { toast } from "sonner";
import { apiRoutes } from "util/apiRoutes";

interface CreateTeabateArgumentProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess: () => void;
  teabate: Impact;
  argumentType: "agree" | "disagree";
  onReopenModal?: () => void;
}

const CreateTeabateArgument: React.FC<CreateTeabateArgumentProps> = ({
  isOpen,
  onClose,
  onSubmitSuccess,
  teabate,
  argumentType,
  onReopenModal,
}) => {
  const { currentUser, setShowAuthModal, userIsMobile } = useUser();
  
  // State for stance and argument
  const [stance, setStance] = useState<"agree" | "disagree">(argumentType);
  const [argument, setArgument] = useState("");
  //const [coauthors, setCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [argumentPrivacy, setArgumentPrivacy] = useState<PrivacyLevel>("public");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);





  /*const handleCoauthorSelect = (suggestion: CoauthorSuggestion) => {
    if (!coauthors.some(c => c.id === suggestion.id && c.type === suggestion.type)) {
      setCoauthors([...coauthors, suggestion]);
    }
  };

  const handleCoauthorRemove = (coauthor: CoauthorSuggestion) => {
    setCoauthors(coauthors.filter(c => !(c.id === coauthor.id && c.type === coauthor.type)));
  };*/

  const handleArgumentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) {
        setShowAuthModal(true);
        return;
    }
    if (!argument.trim()) {
        setError("Please enter your argument.");
        return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await api.post(apiRoutes.comments.teabateArgument(teabate.id), {
        content: argument,
        argument_type: stance,
        is_anonymous: argumentPrivacy === 'anonymous',
        properties: {
          privacy: argumentPrivacy || 'public',
        },
      });
      //const newComment = response.data;

      /*if (coauthors.length > 0) {
        await api.post(apiRoutes.coauthors.request("comment", newComment.id), {
            addedCoauthors: coauthors.map(c => ({ id: c.id, type: c.type })),
        });
      }*/
      
      setArgument("");
      //setCoauthors([]);
      setArgumentPrivacy(currentUser?.properties?.privacy_content_default_audience?.value || "public");
      
      onSubmitSuccess();
      setHasSubmitted(true);
      
      // Close modal and show toast
      onClose();
      
      const stanceText = stance === "agree" ? "supporting" : "opposing";
      
      toast.success(`Your ${stanceText} argument was added.`, {
        duration: 3200,
        action: onReopenModal ? {
          label: "Add another",
          onClick: () => onReopenModal(),
        } : undefined,
      });
    } catch (err: any) {
      console.error("Error creating teabate argument:", err);
      setError(err.response?.data?.message || "Failed to submit argument. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalTitle = (
    <div className="flex items-center">
      {stance === 'agree' 
        ? <CircleCheck className="inline-block mr-2 text-green-600" /> 
        : <CircleX className="inline-block mr-2 text-red-600" />}
      Add argument
    </div>
  );
  




  const renderArgumentForm = () => (
    <form onSubmit={handleArgumentSubmit} className="CreateTeabateArgumentForm space-y-6 pt-4">
      <div>
        <label className="block text-sm font-semibold mb-1">
          Claim
        </label>
        <p className="p-3 bg-eoe-icon dark:bg-eoe-dark-icon rounded-xl text-sm">
          {teabate.title}
        </p>
      </div>

      {/* Stance Selection */}
      <div>
        <label className="block text-sm font-semibold mb-2">
          Stance <span className="text-warning-red dark:text-dark-warning-red">*</span>
        </label>
        <div className="flex gap-2 text-sm">
          <button
            type="button"
            onClick={() => setStance('agree')}
            className={cn(
              "flex items-center gap-2 px-2 py-1.5 rounded-full transition-colors",
              stance === 'agree'
                ? "bg-green-600 text-white border-green-600"
                : "bg-gray-200 dark:bg-eoe-dark-icon text-eoe-gray dark:text-eoe-dark-icon-stroke hover:bg-gray-300 dark:hover:bg-eoe-dark-icon-hover"
            )}
          >
            <CircleCheck className="size-4" />
            Agree
          </button>
          <button
            type="button"
            onClick={() => setStance('disagree')}
            className={cn(
              "flex items-center gap-2 px-2 py-1.5 rounded-full transition-colors",
              stance === 'disagree'
                ? "bg-red-600 text-white border-red-600"
                : "bg-gray-200 dark:bg-eoe-dark-icon text-eoe-gray dark:text-eoe-dark-icon-stroke hover:bg-gray-300 dark:hover:bg-eoe-dark-icon-hover"
            )}
          >
            <CircleX className="size-4" />
            Disagree
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold">
          Argument <span className="text-warning-red dark:text-dark-warning-red">*</span>
        </label>
        <TextEditor
          value={argument}
          onChange={setArgument}
          editorType="entity"
          placeholder={`${stance === 'agree' ? 'Supporting' : 'Opposing'} argument`}
        />
      </div>

      <AiArgumentEvaluationPanel
        claim={teabate.title || ""}
        draftArgument={argument}
        issueId={teabate.issue_id ? Number(teabate.issue_id) : undefined}
        onApplyRevision={setArgument}
        stance={stance}
        teabateId={Number(teabate.id)}
      />

      {/* Coauthors Field */}
      {/*
      <div className="form-control">
        <div className="flex flex-col gap-2">
          <CoauthorSelect
            inputType="add"
            placeholder="Add collaborators"
            className="w-full"
            onSelect={handleCoauthorSelect}
            excludeIds={coauthors.map(c => c.id)}
          />
          {coauthors.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {coauthors.map((coauthor) => (
                <EntityAvatar
                  key={`${coauthor.type}-${coauthor.id}`}
                  entity={coauthor as any}
                  entityType={coauthor.type}
                  entityChip={true}
                  showLink={false}
                  showCloseChip={true}
                  onEntityChipClose={() => handleCoauthorRemove(coauthor)}
                />
              ))}
            </div>
          )}
        </div>
      </div>*/}
      
      {/* Privacy Field */}
      <div className="form-control">
        <label className="block text-sm font-semibold mb-2">
          Privacy
        </label>
        <EntityPrivacy
          privacy={argumentPrivacy}
          onPrivacyChange={(newPrivacy) => setArgumentPrivacy(newPrivacy)}
          actorType="user"
          editMode={false}
          publicOnly={true}
        />
      </div>

      {error && (
        <div className="p-3 bg-warning-red/10 dark:bg-warning-red/5 rounded-md text-warning-red dark:text-dark-warning-red text-sm">
          <div className="flex items-center">
            <Info className="h-4 w-4 mr-2 flex-shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="btn-secondary"
          disabled={isSubmitting}
        >
          {hasSubmitted ? "Close" : "Cancel"}
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className={isSubmitting ? 'btn-primary-disabled' : 'btn-primary'}
        >
          {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : 'Submit'}
        </button>
      </div>
    </form>
  );

  const FormContent = (
      <div className="teabate-argument-form-content p-3 w-full">
          {renderArgumentForm()}
      </div>
  );

  if (userIsMobile) {
    return (
      <Drawer
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
      >
        <DrawerContent header={modalTitle}>
          {FormContent}
        </DrawerContent>
      </Drawer>
    );
  } else {
  

  const footer = (
    <div className="flex justify-end gap-3 py-2">
      <button
        type="button"
        onClick={onClose}
        className="btn-secondary"
        disabled={isSubmitting}
      >
        {hasSubmitted ? "Close" : "Cancel"}
      </button>
      <button
        type="submit"
        disabled={isSubmitting}
        onClick={(e) => {
          e.preventDefault();
          // submit the form programmatically
          const form = document.querySelector('.CreateTeabateArgumentForm') as HTMLFormElement | null;
          if (form) {
            form.requestSubmit();
          }
        }}
        className={isSubmitting ? 'btn-primary-disabled' : 'btn-primary'}
      >
        {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : 'Submit'}
      </button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle} footer={footer} stickyFooter>
      {FormContent}
    </Modal>
  );
  }
};

export default CreateTeabateArgument; 